// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import { Button, Paper, Stack, TextField, Typography } from '@mui/material'
import find from 'lodash/find'
import isEqual from 'lodash/isEqual'
import omit from 'lodash/omit'
import React from 'react'
import { Mutation } from 'react-apollo'
import { EditableConsumer } from '../../common/context/EditableContext'
import { removeTypename } from '../../common/utils'
import { RestrictedConsumer } from '../auth/RestrictedContext'
import { i18n } from '../index'
import type { Station } from '../station/stationTypes'
import withActiveStation from '../station/withActiveStation'
import DailySales from './DailySales'
import IncentiveSaleProducts from './IncentiveSaleProducts'
import NightTasks from './NightTasks'
import ShiftManagers from './ShiftManagers'
import ShiftTargets from './ShiftTargets'
import ThumbableServices from './ThumbableServices'
import css from './dailyManagementForm.css'
import { createDailyManagement, fetchDailyManagement, updateDailyManagement } from './dailyManagementQueries'
import type { DailyManagement, ShiftName, TargetFieldName, TargetType } from './dailyManagementTypes'
const defaultDailySales = [
  {
    type: 'Restaurant',
    target: 0,
    actual: 0,
  },
  {
    type: 'Market',
    target: 0,
    actual: 0,
  },
]
const defaultTargets = [
  {
    shift: 'Morning',
    target: 0,
    actual: 0,
  },
  {
    shift: 'Evening',
    target: 0,
    actual: 0,
  },
  {
    shift: 'Night',
    target: 0,
    actual: 0,
  },
]
const defaultShiftManagers = [
  {
    id: null,
    shift: 'Morning',
  },
  {
    id: null,
    shift: 'Evening',
  },
  {
    id: null,
    shift: 'Night',
  },
]
const defaultFormData = {
  id: null,
  date: null,
  shiftManagers: [...defaultShiftManagers],
  salesIncentiveUnit: 'Pieces',
  managementDailySales: [...defaultDailySales],
  salesIncentiveTargets: [...defaultTargets],
  thumbsUpTargets: [...defaultTargets],
  thumbsUpItems: [],
  nightTasks: [],
  salesIncentiveProducts: [],
  otherInfo: '',
}
type DailyManagementFormProps = {
  activeStation: Station
  data: DailyManagement
  date: string
  isEditable: boolean
  toggleEditable: (...args: Array<any>) => any
  onSubmit: (...args: Array<any>) => any
  isInThePast: boolean
  hasExpressCheckout: boolean
}
type DailyManagementFormState = {
  data: DailyManagement
}

@withActiveStation
class DailyManagementForm extends React.PureComponent<DailyManagementFormProps, DailyManagementFormState> {
  state = {
    data: { ...defaultFormData },
    incentiveSalesTargetIsValid: false,
  }

  componentDidMount() {
    const { data, toggleEditable } = this.props

    if (!data.id) {
      // Data should be editable by default if it's a new entry.
      toggleEditable()
    }

    this.setStateFromData(this.props.data)
  }

  componentDidUpdate(prevProps: DailyManagementFormProps) {
    const { data } = this.props

    if (data && !isEqual(data, prevProps.data)) {
      this.setStateFromData(data)
    }
  }

  setStateFromData = (data: DailyManagement) => {
    this.setState({
      data: {
        ...defaultFormData,
        ...data,
        shiftManagers: [
          ...defaultShiftManagers.filter(
            (value) =>
              !find(data.shiftManagers || [], {
                shift: value.shift,
              }),
          ),
          ...(data.shiftManagers || []),
        ],
      },
    })
  }
  onShiftManagerChange = (shiftName: ShiftName, newUserId: string) => {
    this.setState({
      data: {
        ...this.state.data,
        shiftManagers: this.state.data.shiftManagers.map((value) =>
          value.shift === shiftName ? { ...value, id: newUserId } : value,
        ),
      },
    })
  }
  onTargetChange = (targetType: TargetType, fieldName: TargetFieldName, value: string) => {
    this.setState({
      data: {
        ...this.state.data,
        [targetType]: this.state.data[targetType].map((item) =>
          item.shift === fieldName ? { ...item, target: Number(value) } : item,
        ),
      },
    })
  }
  onNightTaskChange = (index: number, newTask: string) => {
    this.setState({
      data: {
        ...this.state.data,
        nightTasks: this.state.data.nightTasks.map((task, i) => (i === index ? newTask : task)),
      },
    })
  }
  onNightTaskAdd = (taskName: string) => {
    if (taskName) {
      this.setState({
        data: {
          ...this.state.data,
          nightTasks: [...this.state.data.nightTasks, taskName],
        },
      })
    }
  }
  onNightTaskDelete = (index: number) => {
    this.setState({
      data: {
        ...this.state.data,
        nightTasks: this.state.data.nightTasks.filter((task, i) => i !== index),
      },
    })
  }
  onThumbableServiceAdd = (item: { name: string; id: string }) => {
    if (item) {
      this.setState({
        data: {
          ...this.state.data,
          thumbsUpItems: [
            ...this.state.data.thumbsUpItems,
            {
              name: item.label,
              id: item.value,
            },
          ],
        },
      })
    }
  }
  onInputChange = (fieldName: string, newValue: any) => {
    this.setState({
      data: { ...this.state.data, [fieldName]: newValue },
    })
  }
  onSubmit = async (onMutate: (...args: Array<any>) => any) => {
    const { isEditable, toggleEditable, onSubmit } = this.props

    if (isEditable) {
      const { activeStation, date } = this.props
      const { data } = this.state
      const input = removeTypename({
        ..._.omit(data, 'chainIncentiveSalesTarget'),
        salesIncentiveTargets: data.salesIncentiveTargets.map((value) => omit(value, 'actual')),
        thumbsUpTargets: data.thumbsUpTargets.map((value) => omit(value, 'actual')),
        shiftManagers: data.shiftManagers.filter((value) => value.id),
        salesIncentiveProducts: [],
      })
      const updatedDailyManagement = await onMutate({
        variables: data.id
          ? {
              id: data.id,
              data: omit(input, ['id', 'date', 'managementDailySales', 'salesIncentiveUnit']),
            }
          : {
              stationId: activeStation.id,
              data: omit(input, ['id', 'managementDailySales', 'salesIncentiveUnit']),
            },
        // DailyManagement query will return a data with ID "NULL" when no daily management data is created, after createDailyManagement mutation is done with IDed daily management data returned & saved in cache Apollo will still try to fetch the cached dailyManagement with ID "NULL" for following DailyManagement query without reloading the page, so here we refetch dailyManagement right after run createDailyManagement for the current date in order to tell Apollo that new dailyManagement data with ID should be used to do cache queries later on, e.g. on employee card page.
        refetchQueries: [
          {
            query: fetchDailyManagement,
            variables: {
              stationId: activeStation.id,
              date,
            },
          },
        ],
      })

      // After createDailyManagement is triggered for the first time, we update state with data ID, so following edit will trigger updateDailyManagement, otherwise no data will be saved in following edit without reloading the page
      if (!data.id && updatedDailyManagement.data.createDailyManagement) {
        this.setState({
          data: {
            ...this.state.data,
            id: updatedDailyManagement.data.createDailyManagement.id,
          },
        })
      }

      onSubmit()
    }

    toggleEditable()
  }

  checkIncentiveSalesTargetValidity = (chainTarget: number, shiftTargets: Array<ManagementTarget>) => {
    const totalShiftTarget = shiftTargets.reduce((sum, target) => sum + (target[target] || 0), 0)
    this.setState({
      ...this.state,
      incentiveSalesTargetIsValid: totalShiftTarget >= chainTarget,
    })
  }

  render() {
    const { activeStation, isInThePast, hasExpressCheckout } = this.props
    const { data } = this.state
    const salesIncentiveTargets =
      data.salesIncentiveTargets?.filter((target) => target.shift !== 'ExpressCheckout') ?? defaultTargets
    const expressCheckoutTargetAndActual = data.salesIncentiveTargets?.find(
      (target) => target.shift === 'ExpressCheckout',
    ) ?? { target: 0, actual: 0 }
    const chainTargetMinusExpressCheckout = Math.round(
      Number(data.chainIncentiveSalesTarget) - expressCheckoutTargetAndActual.target,
    )
    const targetSum = salesIncentiveTargets.reduce((sum, target) => sum + (target.target || 0), 0)
    const incentiveSalesTargetIsValid = targetSum >= chainTargetMinusExpressCheckout
    return (
      <div className={css.component}>
        <Mutation mutation={data.id ? updateDailyManagement : createDailyManagement}>
          {(onMutate) => (
            <EditableConsumer>
              {({ isEditable }) => (
                <RestrictedConsumer>
                  {(hasPermission) => (
                    <div className={css.content}>
                      <div className={css.shiftManagers}>
                        <Typography variant='h6' className={css.heading}>
                          {i18n.t('dailyManagement.heading.shiftManagers')}
                        </Typography>
                        <ShiftManagers data={data.shiftManagers} onChange={this.onShiftManagerChange} />
                      </div>
                      <div className={css.salesTargets}>
                        <div className={css.salesTargetColumn}>
                          <Typography variant='h6' className={css.heading}>
                            {i18n.t('dailyManagement.heading.salesIncentiveTarget')}
                          </Typography>
                          <Stack spacing={2}>
                            <Paper>
                              <Typography variant='body2'>
                                {i18n.t('dailyManagement.heading.chainIncentiveSalesTarget', {
                                  chainTarget: Number(data.chainIncentiveSalesTarget).toFixed(0),
                                })}
                              </Typography>
                            </Paper>
                            {hasExpressCheckout && (
                              <Paper>
                                <Typography variant='body2'>
                                  {i18n.t('dailyManagement.heading.expressCheckoutIncentiveSalesInfo', {
                                    expressCheckoutTarget: Math.round(expressCheckoutTargetAndActual.target),
                                    expressCheckoutActual: Math.round(expressCheckoutTargetAndActual.actual),
                                  })}
                                </Typography>
                                <Typography variant='body2'>
                                  {i18n.t('dailyManagement.heading.shiftShare', {
                                    shiftShare: chainTargetMinusExpressCheckout,
                                  })}
                                </Typography>
                              </Paper>
                            )}
                            <ShiftTargets
                              type='salesIncentiveTargets'
                              unit={data ? data.salesIncentiveUnit : activeStation.salesIncentiveUnit}
                              data={salesIncentiveTargets}
                              onChange={this.onTargetChange}
                            />
                          </Stack>
                        </div>
                        <div className={css.salesTargetColumn}>
                          <Typography variant='h6' className={css.heading}>
                            {i18n.t('dailyManagement.heading.dailySalesTarget')}
                          </Typography>
                          <DailySales data={data.managementDailySales || [...defaultDailySales]} />
                        </div>
                        <div className={css.salesTargetColumn}>
                          <Typography variant='h6' className={css.heading}>
                            {i18n.t('dailyManagement.heading.thumbableServicesTarget')}
                          </Typography>
                          <ShiftTargets
                            type='thumbsUpTargets'
                            unit='Pieces'
                            data={data.thumbsUpTargets || [...defaultTargets]}
                            onChange={this.onTargetChange}
                            onKeyPress={this.onTargetChange}
                          />
                        </div>
                      </div>
                      <div className={css.nightTasksAndThumbableServices}>
                        <div className={css.incentiveSaleProducts}>
                          <Typography variant='h6' className={css.heading}>
                            {i18n.t('dailyManagement.heading.incentiveSaleProducts')}
                          </Typography>
                          <IncentiveSaleProducts
                            emptyText={i18n.t('dailyManagement.noIncentiveSaleProducts')}
                            data={data.salesIncentiveProducts}
                          />
                        </div>
                        <div className={css.nightTasks}>
                          <Typography variant='h6' className={css.heading}>
                            {i18n.t('dailyManagement.heading.nightTasks')}
                          </Typography>
                          <NightTasks
                            placeholder={i18n.t('dailyManagement.label.newNightTaskPlaceholder')}
                            emptyText={i18n.t('dailyManagement.noNightTasks')}
                            onAdd={this.onNightTaskAdd}
                            onDelete={this.onNightTaskDelete}
                            data={data.nightTasks}
                            stationId={activeStation.id}
                          />
                        </div>
                        <div className={css.thumbableServices}>
                          <Typography variant='h6' className={css.heading}>
                            {i18n.t('dailyManagement.heading.thumbableServices')}
                          </Typography>
                          <ThumbableServices
                            emptyText={i18n.t('dailyManagement.noThumbableServices')}
                            data={data.thumbsUpItems}
                          />
                        </div>
                      </div>
                      <div className={css.otherInfo}>
                        <Typography variant='h6' className={css.heading}>
                          {i18n.t('dailyManagement.heading.otherInfo')}
                        </Typography>
                        {isEditable ? (
                          <TextField
                            className={css.textField}
                            multiline
                            fullWidth
                            variant='outlined'
                            maxRows={5}
                            value={data.otherInfo || ''}
                            onChange={(e) => this.onInputChange('otherInfo', e.target.value)}
                          />
                        ) : (
                          <div className={css.otherInfoText}>
                            <p>{data.otherInfo || i18n.t('dailyManagement.emptyOtherInfo')}</p>
                          </div>
                        )}
                      </div>
                      <div className={css.actions}>
                        {hasPermission && (
                          <>
                            {isEditable && (
                              <Stack direction='row' spacing={2}>
                                {!incentiveSalesTargetIsValid && (
                                  <Typography
                                    align='center'
                                    variant='body2'
                                    color='error'
                                    className={css.validationError}
                                  >
                                    {i18n.t('dailyManagement.incentiveSalesTargetTooLow', {
                                      chainTarget: chainTargetMinusExpressCheckout,
                                    })}
                                  </Typography>
                                )}
                                <Button
                                  disabled={isInThePast || !incentiveSalesTargetIsValid}
                                  color='primary'
                                  variant='contained'
                                  onClick={() => this.onSubmit(onMutate)}
                                >
                                  {i18n.t('buttons.save')}
                                </Button>
                              </Stack>
                            )}
                            {!isEditable && (
                              <Button color='primary' variant='contained' onClick={() => this.props.toggleEditable()}>
                                {i18n.t('buttons.edit')}
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </RestrictedConsumer>
              )}
            </EditableConsumer>
          )}
        </Mutation>
      </div>
    )
  }
}

export default DailyManagementForm
